import { ArchiveCreativeEntity } from "./ArchiveCreativeEntity";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2, Sparkles } from "@digital-twins/design-system/icons";
import {
  CreativeEditorDialog,
  CreativeEditorSection,
} from "./CreativeEditorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CreativeItem } from "@digital-twins/shared-types";
import { ParentBrandImport } from "./ParentBrandImport";
import { BrandMaterials } from "./BrandMaterials";
import { CreativeUpload } from "./CreativeUpload";
import { Textarea } from "@/components/ui/textarea";
import { Text } from "@/components/ui/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MaterialHelpDialog } from "@/components/creative-materials/MaterialHelpDialog";
import { BrandTypography } from "./BrandTypography";
import { BrandPaletteEditor } from "./BrandPaletteEditor";
import { BrandfetchImport } from "./BrandfetchImport";
import { useAttachedReferences } from "./useAttachedReferences";
import { CREATIVE_REFERENCE_LIMIT } from "@digital-twins/shared-types";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type {
  BrandEditorProps,
  BrandfetchLogoChoice,
} from "@/types/creative-library";

export function BrandEditor({
  open,
  brand,
  brands,
  creatives,
  onClose,
  onSaved,
}: BrandEditorProps) {
  const { t } = useTranslation("app");
  const [name, setName] = useState("");
  const [parent, setParent] = useState("none");
  const parentBrand = brands.find(
    (candidate) =>
      candidate.token === parent && candidate.token !== brand?.token,
  );
  const [guidance, setGuidance] = useState("");
  const [typography, setTypography] = useState("");
  const [colors, setColors] = useState<Array<{ name: string; value: string }>>(
    [],
  );
  const [materials, setMaterials] = useState<string[]>([]);
  const [logoUpload, setLogoUpload] = useState(false);
  const [uploadedMaterials, setUploadedMaterials] = useState<CreativeItem[]>(
    [],
  );
  const listedMaterials = [...uploadedMaterials, ...creatives];
  const attached = useAttachedReferences(
    open,
    materials,
    listedMaterials,
    brand?.hiddenReferenceTokens,
  );
  const availableMaterials = Array.from(
    new Map(
      [...attached.found, ...listedMaterials].map((item) => [item.token, item]),
    ).values(),
  );
  const [isImportingLogos, setIsImportingLogos] = useState(false);
  const [logosOverLimit, setLogosOverLimit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setLogoUpload(false);
      setLogosOverLimit(false);
      setUploadedMaterials([]);
      setName(brand?.name ?? "");
      setParent(brand?.parentBrandToken ?? "none");
      setGuidance(brand?.guidance ?? "");
      setTypography(brand?.typography ?? "");
      setColors(brand?.palette ?? []);
      setMaterials(brand?.materialTokens ?? []);
      setError("");
    }
  }, [open, brand]);
  async function importLogos(logoImport: {
    domain: string;
    logos: Array<BrandfetchLogoChoice>;
  }) {
    setIsImportingLogos(true);
    setLogosOverLimit(false);
    setError("");
    try {
      const items = await creativeLibraryApi.importBrandfetchLogos({
        ...logoImport,
        brandToken: brand?.token ?? null,
      });
      setUploadedMaterials((previous) => [...previous, ...items]);
      setLogosOverLimit(materials.length + items.length > CREATIVE_REFERENCE_LIMIT);
      setMaterials((previous) =>
        [...previous, ...items.map((item) => item.token)].slice(
          0,
          CREATIVE_REFERENCE_LIMIT,
        ),
      );
      onSaved();
    } catch {
      setError(t("creativeLibrary.brandfetchLogosFailed"));
    } finally {
      setIsImportingLogos(false);
    }
  }
  async function save() {
    setSaving(true);
    setError("");
    const body = {
      name: name.trim(),
      parentBrandToken: parent === "none" ? null : parent,
      guidance,
      typography,
      palette: colors,
      materialTokens: materials,
    };
    try {
      if (brand)
        await creativeLibraryApi.updateBrand(brand.token, {
          ...body,
          revision: brand.revision,
        });
      else await creativeLibraryApi.createBrand(body);
      onSaved();
      onClose();
    } catch {
      setError(t("creativeLibrary.saveFailed"));
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
      <CreativeEditorDialog
        open={open && !logoUpload}
        busy={saving}
        onClose={onClose}
        kind="brands"
        title={t(
          brand ? "creativeLibrary.editBrand" : "creativeLibrary.newBrand",
        )}
        description={t("creativeLibrary.brandEditorHint")}
        testId="creative-brand-editor"
        footer={
          <>
            {brand?.canEdit && (
              <ArchiveCreativeEntity
                kind="brands"
                token={brand.token}
                disabled={saving}
                onArchived={() => {
                  onSaved();
                  onClose();
                }}
              />
            )}
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="creative-brand-cancel"
            >
              {t("creativeLibrary.cancel")}
            </Button>
            <Button
              variant="primary"
              className="gap-2"
              onClick={() => void save()}
              disabled={
                !name.trim() || name.length > 255 || saving || isImportingLogos
              }
              data-testid="creative-brand-save"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("creativeLibrary.saveBrand")}
            </Button>
          </>
        }
      >
        <BrandfetchImport
          onApply={(importedName, importedColors, logoImport) => {
            setName(importedName);
            setColors(importedColors);
            if (logoImport) {
              void importLogos(logoImport);
            }
          }}
        />
        {isImportingLogos && (
          <Text
            size="xs"
            color="muted"
            className="flex items-center gap-2"
            data-testid="brandfetch-logos-importing"
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("creativeLibrary.brandfetchLogosImporting")}
          </Text>
        )}
        {logosOverLimit && (
          <Text
            size="xs"
            color="muted"
            data-testid="brandfetch-logos-over-limit"
          >
            {t("creativeLibrary.brandfetchLogosOverLimit")}
          </Text>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="brand-name">{t("creativeLibrary.name")}</Label>
            <Input
              autoFocus
              id="brand-name"
              data-testid="creative-brand-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>{t("creativeLibrary.parentBrand")}</Label>
            <Select value={parent} onValueChange={setParent}>
              <SelectTrigger
                aria-label={t("creativeLibrary.parentBrand")}
                data-testid="creative-brand-parent"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t("creativeLibrary.standaloneBrand")}
                </SelectItem>
                {brands
                  .filter((b) => b.token !== brand?.token)
                  .map((b) => (
                    <SelectItem key={b.token} value={b.token}>
                      {b.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {parentBrand && (
          <ParentBrandImport
            key={parentBrand.token}
            parent={parentBrand}
            onApply={(settings) => {
              if (settings.palette !== undefined) setColors(settings.palette);
              if (settings.typography !== undefined)
                setTypography(settings.typography);
              if (settings.guidance !== undefined)
                setGuidance(settings.guidance);
            }}
          />
        )}
        <BrandMaterials
          creatives={availableMaterials}
          selected={materials}
          unavailable={attached.unavailable}
          privateTokens={attached.hidden}
          onChange={setMaterials}
          onUploadLogo={() => setLogoUpload(true)}
        />
        <CreativeEditorSection>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles
                className="h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Label htmlFor="brand-guidance">
                {t("creativeLibrary.brandGuidance")}
              </Label>
            </div>
            <MaterialHelpDialog kind="style" />
          </div>
          <Textarea
            id="brand-guidance"
            data-testid="creative-brand-guidance"
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            rows={3}
            placeholder={t("creativeLibrary.guidancePlaceholder")}
            maxLength={2000}
          />
        </CreativeEditorSection>
        <div className="grid items-start gap-4 sm:grid-cols-2">
          <BrandTypography value={typography} onChange={setTypography} />
          <BrandPaletteEditor colors={colors} onChange={setColors} />
        </div>
        {error && (
          <Text
            size="sm"
            className="flex items-center gap-2 text-destructive"
            role="alert"
          >
            <AlertCircle className="h-4 w-4" />
            {error}
          </Text>
        )}
      </CreativeEditorDialog>
      <CreativeUpload
        open={open && logoUpload}
        presetRole="logo"
        presetBrandToken={brand?.token ?? "none"}
        brands={brands}
        products={[]}
        onClose={() => setLogoUpload(false)}
        onSaved={onSaved}
        onUploaded={(item) => {
          setUploadedMaterials((previous) => [...previous, item]);
          setMaterials((previous) => [...previous, item.token]);
        }}
      />
    </>
  );
}
